/**
 * Carga máscara Vol3D: JSON pre-resampleado, octet-stream N³, o DICOM-SEG.
 */
import dicomParser from 'dicom-parser'
import { resampleMaskToGrid } from './mpr3dSegCoords'

const SEG_SOP = '1.2.840.10008.5.1.4.1.1.66.4'

function str(ds, tag) {
  try {
    const el = ds.elements[tag]
    if (!el) return ''
    return String(ds.string(tag) || '').trim()
  } catch (e) {
    return ''
  }
}

function num(ds, tag, fallback) {
  const v = Number(str(ds, tag))
  return Number.isFinite(v) ? v : fallback
}

function looksDicom(buf) {
  if (!buf || buf.length < 132) return false
  const mag = String.fromCharCode(buf[128], buf[129], buf[130], buf[131])
  return mag === 'DICM'
}

function unpackBits(bytes, rows, cols, frames) {
  const plane = rows * cols
  const out = new Uint8Array(plane * frames)
  let bit = 0
  for (let i = 0; i < out.length; i++) {
    const b = bytes[bit >> 3] || 0
    out[i] = (b >> (bit & 7)) & 1
    bit++
  }
  return out
}

function pixelBytes(ds) {
  const el = ds.elements.x7fe00010
  if (!el) return null
  if (el.fragments && el.fragments.length) {
    let n = 0
    for (let i = 0; i < el.fragments.length; i++) n += el.fragments[i].length
    const out = new Uint8Array(n)
    let o = 0
    for (let i = 0; i < el.fragments.length; i++) {
      const f = el.fragments[i]
      out.set(ds.byteArray.slice(f.position, f.position + f.length), o)
      o += f.length
    }
    return out
  }
  const off = el.dataOffset
  const len = el.length
  if (!(len > 0)) return null
  return ds.byteArray.slice(off, off + len)
}

/**
 * Parsea DICOM-SEG (binario 1 bit o 8 bit, no encapsulado JPEG) → labelmap nativo.
 */
export function parseDicomSeg(byteArray) {
  const ds = dicomParser.parseDicom(byteArray)
  const sop = str(ds, 'x00080016')
  const cols = num(ds, 'x00280011', 0)
  const rows = num(ds, 'x00280010', 0)
  const frames = Math.max(1, num(ds, 'x00280008', 1))
  const bits = num(ds, 'x00280100', 8)
  const raw = pixelBytes(ds)
  if (!cols || !rows || !raw) {
    throw new Error('DICOM-SEG sin Pixel Data o matriz.')
  }
  let labels
  if (bits === 1) labels = unpackBits(raw, rows, cols, frames)
  else {
    labels = new Uint8Array(rows * cols * frames)
    const n = Math.min(labels.length, raw.length)
    for (let i = 0; i < n; i++) labels[i] = raw[i] ? (raw[i] > 1 ? 1 : raw[i]) : 0
  }
  return {
    sop,
    isSeg: sop === SEG_SOP || sop.indexOf('1.2.840.10008.5.1.4.1.1.66') === 0,
    cols,
    rows,
    zDim: frames,
    labels,
  }
}

function fromJson(obj, n) {
  if (!obj) return null
  const N = Math.max(8, Number(obj.n || obj.size) || n)
  let data = obj.data || obj.mask
  if (typeof data === 'string') {
    const bin = typeof atob === 'function' ? atob(data) : Buffer.from(data, 'base64').toString('binary')
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    data = out
  }
  if (!data || !data.length) return null
  if (data.length === N * N * N) return new Uint8Array(data)
  const side = Math.round(Math.cbrt(data.length))
  return resampleMaskToGrid(data, { cols: side, rows: side, zDim: side }, N, false)
}

/**
 * @param {Uint8Array|ArrayBuffer} buf
 * @param {{ size: number }} vol
 */
export function decodeMaskBuffer(buf, vol) {
  const n = vol && vol.size ? vol.size | 0 : 0
  if (!n) throw new Error('Sin volumen 3D para la máscara.')
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  if (bytes.length >= 2 && bytes[0] === 0x7b) {
    const text = new TextDecoder('utf-8').decode(bytes)
    const json = JSON.parse(text)
    const mask = fromJson(json, n)
    if (!mask) throw new Error('JSON de máscara vacío.')
    return mask
  }
  if (looksDicom(bytes)) {
    const seg = parseDicomSeg(bytes)
    if (seg.labels.length === n * n * n) return seg.labels
    return resampleMaskToGrid(
      seg.labels,
      { cols: seg.cols, rows: seg.rows, zDim: seg.zDim },
      n,
      false
    )
  }
  if (bytes.length === n * n * n) return new Uint8Array(bytes)
  const side = Math.round(Math.cbrt(bytes.length))
  if (side * side * side === bytes.length && side >= 8) {
    return resampleMaskToGrid(bytes, { cols: side, rows: side, zDim: side }, n, false)
  }
  throw new Error('Formato de máscara no reconocido (SEG, JSON N³ o raw N³).')
}

export async function fetchMaskFromUrl(url, vol) {
  if (!url) throw new Error('Sin URL de máscara.')
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) throw new Error(`Máscara HTTP ${res.status}`)
  const buf = new Uint8Array(await res.arrayBuffer())
  return decodeMaskBuffer(buf, vol)
}
